import produce from "immer";
import * as React from "react";

import { useRelodeApp } from "./";

export const RelodeAppUIContext = React.createContext({});

interface RelodeAppUIContextShape {
    pageTitle: string;
    isBusy: boolean;
    actions: {
        incrementIsBusy: () => void;
        decrementIsBusy: () => void;
        setPageTitle: (title: string | undefined) => void;
    };
}

export const RelodeAppUI = props => {
    const {
        state: {
            config: { appName },
        },
    } = useRelodeApp();
    const [state, dispatch] = React.useReducer(reducer, {
        isBusy: false,
        loadingCount: 0,
        pageTitle: `${appName} | relode`,
    });

    const value = React.useMemo(
        () => ({
            ui: {
                pageTitle: state.pageTitle,
                isBusy: state.isBusy,
                actions: {
                    incrementIsBusy: () =>
                        dispatch({ type: "LOADING_INCREMENT" }),
                    decrementIsBusy: () =>
                        dispatch({ type: "LOADING_DECREMENT" }),
                    setPageTitle: (title: string) => {
                        const newTitle = !!title ? title : appName;
                        dispatch({
                            type: "PAGE_TITLE_SET",
                            title: `${newTitle} | relode`,
                        });
                    },
                },
            },
        }),
        [state, appName]
    );

    return <RelodeAppUIContext.Provider value={value} {...props} />;
};

export const useRelodeAppUI = () => {
    const context: any = React.useContext(RelodeAppUIContext);
    if (context === undefined || context.ui === undefined) {
        throw new Error(
            "useRelodeAppUI must be used within a RelodeAppUI context"
        );
    }

    return context as { ui: RelodeAppUIContextShape };
};

type RelodeAppUIActionsTypes =
    | { type: "LOADING_INCREMENT" }
    | { type: "LOADING_DECREMENT" }
    | { type: "PAGE_TITLE_SET"; title: string }
    | { type: "USER_LOGOUT" };

interface State {
    isBusy: boolean;
    loadingCount: number;
    pageTitle: string;
}

const reducer = (state: State, action: RelodeAppUIActionsTypes) =>
    produce(state, draft => {
        switch (action.type) {
            case "LOADING_DECREMENT": {
                draft.loadingCount--;
                draft.isBusy = draft.loadingCount > 0;
                break;
            }

            case "LOADING_INCREMENT": {
                draft.loadingCount++;
                draft.isBusy = draft.loadingCount > 0;
                break;
            }

            case "PAGE_TITLE_SET": {
                draft.pageTitle = action.title;
                break;
            }
        }
    });
